import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink, Loader } from 'lucide-react';
import { priceDisplay } from '../../plans/planCatalog';
import { openExternal } from '../../utils/openExternal';
import './OrderConsentStep.css';

/**
 * The last step before Stripe: the § 8 (1) FAGG summary, the § 16 / § 18
 * express request to start immediately, and an order button that says it costs
 * money (§ 8 (2) FAGG). Wording: "Must-do changes (by law) — proposal", item 3.
 *
 * WHY THE BOX MUST BE TICKED TO ORDER. Apiboo starts the moment checkout
 * completes, and without the express request a consumer who withdraws owes
 * nothing for the days used (§ 16 (2) FAGG). The proposal says the box is "not
 * pre-ticked; required to continue", so the order button stays disabled until
 * the user ticks it themselves, and the line under the box says why. What the
 * backend gets is still what the user did — `immediate_start_consent` is the
 * box's own state, never a constant — plus the version of the text they saw.
 *
 * Bump CONSENT_TEXT_VERSION whenever the consent or summary wording changes:
 * it is the evidence of WHICH text was agreed to.
 */
export const CONSENT_TEXT_VERSION = 'fagg-immediate-start-2026-10-17';

export const TERMS_URL = 'https://apiboo.com/terms';
// The withdrawal notice and the public "Withdraw from contract" form (Terms § 4).
export const WITHDRAWAL_URL = 'https://apiboo.com/withdraw';

/** "PRO" → "Pro": the catalogue's names are shouted for the cards. */
export function displayPlanName(plan) {
  const name = String(plan?.name || plan?.id || '');
  return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : '';
}

/** `consent_text_version` as sent: the text version plus the language it was shown in. */
export function consentTextVersion(language) {
  const lang = String(language || 'en').slice(0, 2).toLowerCase() === 'de' ? 'de' : 'en';
  return `${CONSENT_TEXT_VERSION}:${lang}`;
}

export default function OrderConsentStep({ plan, cycle, currency, busy, phase, onBack, onConfirm }) {
  const { t, i18n } = useTranslation();
  const [requested, setRequested] = useState(false);
  const checkboxId = useId();
  const hintId = useId();

  const annual = cycle === 'annual';
  const perSeat = plan.id === 'team';
  const { price } = priceDisplay(plan, cycle, currency, t);
  const planName = displayPlanName(plan);
  const priceKey = `legal.order.price${perSeat ? 'Seat' : ''}${annual ? 'Annual' : 'Monthly'}`;

  const submit = () => {
    if (busy || !requested) return;
    onConfirm({
      immediateStartConsent: requested === true,
      consentTextVersion: consentTextVersion(i18n.language),
    });
  };

  return (
    <div className="order-step" data-testid="order-consent-step">
      <h3 className="order-step-title">{t('legal.order.title')}</h3>

      <div className="order-summary" data-testid="order-summary">
        <p className="order-summary-price">
          <strong>{t(priceKey, { plan: planName, price })}</strong>{' '}
          <span>{t('legal.order.finalPrice')}</span>
        </p>
        <p>{t(annual ? 'legal.order.termsAnnual' : 'legal.order.termsMonthly')}</p>
        <p>{t('legal.order.withdrawalInfo')}</p>
      </div>

      <div className="order-consent">
        <input
          id={checkboxId}
          type="checkbox"
          checked={requested}
          onChange={(e) => setRequested(e.target.checked)}
          disabled={busy}
          aria-describedby={hintId}
          data-testid="order-immediate-start"
        />
        <label htmlFor={checkboxId}>{t('legal.order.consent', { plan: planName })}</label>
      </div>
      <p id={hintId} className="order-consent-hint" data-testid="order-consent-hint">
        {t('legal.order.consentRequired')}
      </p>

      <p className="order-links">
        <span>{t('legal.order.linksIntro')}</span>{' '}
        <button type="button" className="order-link" onClick={() => openExternal(TERMS_URL)}>
          {t('legal.order.termsLink')} <ExternalLink size={11} aria-hidden />
        </button>
        <span aria-hidden> · </span>
        <button type="button" className="order-link" onClick={() => openExternal(WITHDRAWAL_URL)}>
          {t('legal.order.withdrawalLink')} <ExternalLink size={11} aria-hidden />
        </button>
      </p>

      <div className="order-actions">
        <button type="button" className="pu-footer-later order-back" onClick={onBack} disabled={busy}>
          <ArrowLeft size={13} aria-hidden /> {t('legal.order.back')}
        </button>
        <button
          type="button"
          className="pu-btn pu-btn--upgrade order-submit"
          onClick={submit}
          disabled={busy || !requested}
          data-testid="order-submit"
        >
          {busy ? (
            <>
              <Loader size={13} className="pu-btn-spinner" />
              {phase === 'redirecting' ? t('legal.order.redirecting') : t('legal.order.processing')}
            </>
          ) : (
            t('legal.order.submit')
          )}
        </button>
      </div>
    </div>
  );
}
